import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { FormikProvider, useFormik } from "formik";
import { FolderUp } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
	PrimaryButton,
	TextPrimaryButton,
} from "src/components/Default/LekUI/Button";
import { Stepper } from "src/components/Default/LekUI/Stepper";
import {
	Modal,
	ModalHeader,
	ModalStickyFooter,
} from "src/components/Modals/Components/ModalsComponents";
import { humanFileSize } from "src/components/Uploader/FileUploader";
import {
	type Flashcard,
	type ImportedDeck,
	parseAnkiDecks,
	parseAnkiFile,
} from "src/helpers/flashcards/ankiImport";
import {
	generateColumnName,
	getColumnLetter,
	groupRowsByDeck,
	readSpreadsheetRows,
	SPREADSHEET_ACCEPT,
} from "src/helpers/flashcards/csvImport";

import { ColumnMapping } from "./ColumnMapping";
import { ImportDropzone } from "./ImportDropzone";
import { ImportedFileCard } from "./ImportedFileCard";
import { ImportPreview } from "./ImportPreview";
import { SectionHeading } from "./SectionHeading";
import { type ImportFormValues, initialValues } from "./types";

const SPREADSHEET_EXT = /\.(csv|xlsx)$/i;
const ANKI_EXT = /\.(apkg|colpkg|txt)$/i;

interface DecksImportModalProps {
	onClose: () => void;
	onConfirm?: (flashcards: Flashcard[], deckName?: string) => void;
	// Tytuł modala — domyślnie "Importuj fiszki". Pozwala nadpisać np. na "Importuj zestawy".
	title?: string;
	// Tryb wielu zestawów: modal zwraca listę zestawów, przyjmuje kilka plików CSV/XLSX i .colpkg.
	onConfirmDecks?: (decks: ImportedDeck[]) => void;
}

export const DecksImportModal = ({
	onClose,
	onConfirm,
	title,
	onConfirmDecks,
}: DecksImportModalProps) => {
	const { t } = useTranslation();
	const multiDeck = !!onConfirmDecks;

	const formik = useFormik<ImportFormValues>({
		initialValues,
		onSubmit: () => {
			if (onConfirmDecks) {
				onConfirmDecks(decks);
			} else {
				onConfirm?.(
					decks.flatMap((deck) => deck.flashcards),
					values.anki?.decks[0]?.name,
				);
			}
			onClose();
		},
	});
	const { values, setValues, setFieldValue } = formik;

	// Kolumny z pierwszego pliku; pozostałe pliki mapowane po indeksie kolumny.
	const columns = useMemo(() => {
		const firstRow = values.files[0]?.rows[0] ?? [];
		return firstRow.map((cell, i) =>
			values.hasHeader && cell.trim() ? cell : generateColumnName(i),
		);
	}, [values.files, values.hasHeader]);

	const decks = useMemo<ImportedDeck[]>(() => {
		if (values.anki) return values.anki.decks;

		const column = (i: number) => (i >= 0 ? columns[i] : "");
		const front = column(values.frontColumn);
		const back = column(values.backColumn);
		return values.files.flatMap((file) => {
			const rows = (values.hasHeader ? file.rows.slice(1) : file.rows)
				.map((row) =>
					Object.fromEntries(columns.map((col, i) => [col, row[i] ?? ""])),
				)
				.filter((row) => row[front]?.trim() && row[back]?.trim());
			return groupRowsByDeck(
				rows,
				multiDeck ? column(values.deckNameColumn) : "",
				values.hasHeader,
				multiDeck ? column(values.deckDescriptionColumn) : "",
			).map((group) => ({
				name: group.name || file.name,
				description: group.description,
				flashcards: group.rows.map((row) => ({
					front: row[front].trim(),
					back: row[back].trim(),
				})),
			}));
		});
	}, [values, columns, multiDeck]);

	const flashcards = decks.flatMap((deck) => deck.flashcards);
	const hasFile = values.files.length > 0 || !!values.anki;

	const handleFiles = async (picked: File[]) => {
		const [first] = picked;
		if (!first) return;
		if (!SPREADSHEET_EXT.test(first.name) && !ANKI_EXT.test(first.name)) {
			toast.error(
				t("flashcardsImport.unsupported", "Nieobsługiwany format pliku"),
			);
			return;
		}

		setFieldValue("isParsing", true);
		try {
			if (SPREADSHEET_EXT.test(first.name)) {
				const sheets = multiDeck
					? picked.filter((f) => SPREADSHEET_EXT.test(f.name))
					: [first];
				const files = await Promise.all(
					sheets.map(async (file) => ({
						name: file.name.replace(/\.[^.]+$/, ""),
						fileName: file.name,
						size: file.size,
						rows: await readSpreadsheetRows(file, file.name),
					})),
				);
				if ((files[0].rows[0]?.length ?? 0) < 2) {
					toast.error(
						t(
							"flashcardsImport.tooFewColumns",
							"Plik musi mieć co najmniej 2 kolumny",
						),
					);
					return;
				}
				setValues({ ...initialValues, hasHeader: values.hasHeader, files });
				return;
			}

			const ankiDecks = multiDeck
				? await parseAnkiDecks(first, first.name)
				: await parseAnkiFile(first, first.name).then(
						({ flashcards, deckName }) => [{ name: deckName, flashcards }],
					);
			if (!ankiDecks.some((deck) => deck.flashcards.length)) {
				toast.error(
					t("flashcardsImport.anki.noCards", "Nie znaleziono fiszek w pliku"),
				);
				return;
			}
			setValues({
				...initialValues,
				anki: { fileName: first.name, size: first.size, decks: ankiDecks },
			});
		} catch {
			toast.error(t("flashcardsImport.error", "Nie udało się odczytać pliku"));
		} finally {
			setFieldValue("isParsing", false);
		}
	};

	const resetFile = () =>
		setValues({ ...initialValues, hasHeader: values.hasHeader });

	const fileCards = values.anki
		? [
				{
					fileName: values.anki.fileName,
					meta: `${humanFileSize(values.anki.size)} · Anki`,
				},
			]
		: values.files.map((file) => ({
				fileName: file.fileName,
				meta: `${humanFileSize(file.size)} · ${t("flashcardsImport.rows", {
					count: file.rows.length,
					defaultValue: "{{count}} wierszy",
				})}`,
			}));

	const renderFileCards = () =>
		fileCards.map((file, i) => (
			<ImportedFileCard
				key={`${file.fileName}-${i}`}
				fileName={file.fileName}
				meta={file.meta}
				onChange={resetFile}
			/>
		));

	// Anki/TXT ma gotowe strony fiszek — pomijamy krok mapowania kolumn.
	const steps = values.anki
		? [
				t("flashcardsImport.steps.file", "Plik"),
				t("flashcardsImport.steps.preview", "Podgląd"),
			]
		: [
				t("flashcardsImport.steps.file", "Plik"),
				t("flashcardsImport.steps.columns", "Kolumny"),
				t("flashcardsImport.steps.preview", "Podgląd"),
			];
	const isLastStep = values.step === steps.length - 1;
	const isColumnsStep = !values.anki && values.step === 1;
	const setStep = (step: number) => setFieldValue("step", step);

	const previewSource = (index: number, column: number) =>
		values.anki
			? t("flashcardsImport.field", { n: index, defaultValue: "pole {{n}}" })
			: t("flashcardsImport.column", {
					letter: getColumnLetter(column),
					defaultValue: "kol. {{letter}}",
				});

	return (
		<Modal
			onClose={onClose}
			maxWidth={720}
			isLoading={values.isParsing}
		>
			<FormikProvider value={formik}>
				<ModalHeader
					Icon={FolderUp}
					title={title ?? t("flashcardsImport.title", "Importuj fiszki")}
					hasCloseButton
				/>

				<div className="flex flex-col gap-20 mt-24">
					{hasFile && <Stepper steps={steps} activeStep={values.step} />}

					{values.step === 0 && (
						<>
							<SectionHeading
								title={t("flashcardsImport.pickFile", "Wybierz plik")}
								description={
									!hasFile
										? t(
												"flashcardsImport.pickFileHint",
												"CSV i XLSX poprosimy o dopasowanie kolumn. Anki i TXT importujemy od razu.",
											)
										: values.anki
											? t(
													"flashcardsImport.fileReadyAnki",
													"Plik gotowy. Przejdź dalej, żeby zobaczyć podgląd.",
												)
											: t(
													"flashcardsImport.fileReady",
													"Plik gotowy. Przejdź dalej, żeby dopasować kolumny.",
												)
								}
							/>
							{hasFile ? (
								renderFileCards()
							) : (
								<ImportDropzone
									accept={`${SPREADSHEET_ACCEPT},.apkg,.txt${multiDeck ? ",.colpkg" : ""}`}
									multiple={multiDeck}
									formats={[
										".csv",
										".xlsx",
										".txt",
										multiDeck ? ".apkg / .colpkg" : ".apkg",
									]}
									onFiles={handleFiles}
								/>
							)}
						</>
					)}

					{isColumnsStep && (
						<>
							<SectionHeading
								title={t("flashcardsImport.mapColumns", "Dopasuj kolumny")}
								description={
									multiDeck
										? t(
												"flashcardsImport.mapColumnsHintDecks",
												"Wskaż kolumny z przodem i tyłem fiszki. Nazwa i opis zestawu są opcjonalne.",
											)
										: t(
												"flashcardsImport.mapColumnsHint",
												"Wskaż kolumny z przodem i tyłem fiszki.",
											)
								}
							/>
							<ColumnMapping columns={columns} multiDeck={multiDeck} />
							<FormControlLabel
								control={
									<Checkbox
										checked={values.hasHeader}
										onChange={(e) =>
											setFieldValue("hasHeader", e.target.checked)
										}
										data-test-id="import-csv-header-checkbox"
									/>
								}
								label={t(
									"csvImport.hasHeader",
									"Pierwszy wiersz zawiera nagłówki",
								)}
							/>
						</>
					)}

					{hasFile && isLastStep && (
						<>
							{values.anki ? (
								<>
									{renderFileCards()}
									<div className="alert--correct border-radius-12 p-16 size-14">
										{t("flashcardsImport.ankiReady", {
											count: flashcards.length,
											defaultValue:
												"Plik Anki ma gotowe strony fiszek – niczego nie trzeba dopasowywać. Wykryto {{count}} fiszek.",
										})}
									</div>
								</>
							) : (
								<SectionHeading
									title={t("flashcardsImport.preview.title", "Sprawdź podgląd")}
									description={t(
										"flashcardsImport.preview.hint",
										"Tak będą wyglądać zaimportowane fiszki.",
									)}
								/>
							)}
							<ImportPreview
								flashcards={flashcards}
								frontSource={previewSource(1, values.frontColumn)}
								backSource={previewSource(2, values.backColumn)}
							/>
						</>
					)}
				</div>

				<ModalStickyFooter className="justify-end gap-12 mt-24 pt-16 border-top-1">
					<div className="flex items-center gap-12">
						{values.step === 0 ? (
							<TextPrimaryButton
								onClick={onClose}
								data-test-id="import-cancel-button"
							>
								{t("commands.cancel", "Anuluj")}
							</TextPrimaryButton>
						) : (
							<TextPrimaryButton
								onClick={() => setStep(values.step - 1)}
								data-test-id="import-back-button"
							>
								{t("commands.back", "Wróć")}
							</TextPrimaryButton>
						)}
						{isLastStep ? (
							<PrimaryButton
								onClick={formik.submitForm}
								disabled={flashcards.length === 0}
								data-test-id="import-confirm-button"
							>
								{t("flashcardsImport.importCount", {
									count: flashcards.length,
									defaultValue: "Importuj {{count}} fiszek",
								})}
							</PrimaryButton>
						) : (
							<PrimaryButton
								onClick={() => setStep(values.step + 1)}
								disabled={
									!hasFile || (isColumnsStep && flashcards.length === 0)
								}
								data-test-id="import-next-button"
							>
								{t("flashcardsImport.next", "Dalej")}
							</PrimaryButton>
						)}
					</div>
				</ModalStickyFooter>
			</FormikProvider>
		</Modal>
	);
};
